import { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ThemeToggle from '@/components/ThemeToggle';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'About', href: '#about' },
  { label: 'Schools', href: '#schools' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Blog', href: '#blog' },
  { label: 'Contact', href: '#contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section tracking
  useEffect(() => {
    const sectionIds = navLinks.map(l => l.href.replace('#', ''));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between h-20">
          <a href="#home" className="flex items-center gap-2">
            <img
              src="/images/logohori.JPG"
              alt="OyinBatfo Branding Solutions"
              className="h-12 object-contain rounded"
            />
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace('#', '');
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 relative ${
                    scrolled
                      ? isActive
                        ? 'text-primary'
                        : 'text-foreground hover:text-primary'
                      : isActive
                        ? 'text-primary'
                        : 'text-primary-foreground hover:text-primary'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary rounded-full" />
                  )}
                </a>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+2348050488734"
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                scrolled ? 'text-foreground' : 'text-primary-foreground'
              }`}
            >
              <Phone className="w-4 h-4" />
              +234 805 048 8734
            </a>
            <ThemeToggle />
            <Button
              asChild
              className="bg-primary hover:bg-primary-glow text-primary-foreground animate-glow-pulse font-semibold"
            >
              <a href="#contact">Get A Quote</a>
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 ${scrolled ? 'text-foreground' : 'text-primary-foreground'}`}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-background/98 backdrop-blur-lg border-b border-border animate-slide-up">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive = activeSection === link.href.replace('#', '');
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-md transition-colors font-medium ${
                      isActive
                        ? 'text-primary bg-primary/5 border-l-2 border-primary'
                        : 'text-foreground hover:text-primary hover:bg-muted'
                    }`}
                  >
                    {link.label}
                  </a>
                );
              })}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <ThemeToggle />
                <Button asChild className="bg-primary text-primary-foreground">
                  <a href="#contact" onClick={() => setMobileOpen(false)}>Get A Quote</a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-40 w-11 h-11 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-xl transition-all duration-300 hover:bg-primary-glow ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    </>
  );
};

export default Navbar;
